import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";

import { isLifecycleFocus } from "../foundations/lifecycleFocus";

/**
 * Tooltip 가시성 엔진 = 공개 show/hide controller. `visible` 상태와 open/close 타이머·focus intent 판정·
 * 트리거 바인딩(`triggerProps`)을 소유한다. `Tooltip`은 `controller` prop을 받지 않으면 내부적으로 이
 * 훅을 써서 hover/focus로 자동 표시하고(auto mode), `controller`를 주입하면 그 controller가 가시성을
 * 구동한다(manual mode). 위치 보정/portal/Escape는 `Tooltip`이 `visible`을 보고 처리한다.
 *
 * manual mode 용례: 앵커가 tooltip과 동시에 다른 popup(메뉴 등)을 여는 경우. `suppressed`로 그 popup이
 * 떠 있는 동안 표시를 억제하면 열린 popup 위에 tooltip이 겹치지 않는다.
 */

// 포인터 hover→표시 지연. "스쳐 지나감"과 "의도적으로 머무름"을 구분해 툴바 sweep flicker를 막는다.
// 키보드 focus는 이미 의도적이라 지연 없이 즉시 표시(show 직접 호출). fade-in이 도착을 부드럽게 하므로
// 게이트를 약간 당겨 반응성을 살린다.
const OPEN_DELAY_MS = 250;
// 포인터가 트리거→버블 dead-zone을 건너는 닫힘 유예(hover bridge, WCAG 1.4.13). exit fade가 pointer-events를
// 유지한 채 되돌림 가능한 꼬리를 더 주므로(Tooltip §fade), 하드 유예는 짧게 둔다.
const HOVER_GRACE_MS = 100;

// scroll로 정지한 포인터 아래 DOM hit target이 바뀌면 브라우저가 scroll 뒤 pointerover/mouseenter를
// 합성할 수 있다. 그 boundary enter를 새 의도로 오인하지 않도록, Tooltip 계열 전체가 공유하는 latch를
// scroll에서 잠그고 다음 실제 pointermove/mousemove에서만 푼다. 인스턴스마다 window listener를 달지
// 않도록 listener 수명은 ref-counted singleton으로 관리한다. latch가 잠긴 동안은 subscriber가 0이 되어도
// release listener만 유지해 비동기 replacement 사이의 실제 이동을 놓치지 않는다. move는 latch만 풀며,
// 실제 open은 이후 leave→enter가 다시 scheduleShow를 호출해야 시작된다.
let pointerIntentSubscriberCount = 0;
let pointerOpenSuppressedAfterScroll = false;
let lastPointerPosition: { x: number; y: number } | null = null;
let pointerIntentListenersAttached = false;

function suppressPointerOpenOnScroll() {
  pointerOpenSuppressedAfterScroll = true;
}

function resumePointerOpenOnMove(event: MouseEvent) {
  // scroll 전에 좌표를 한 번도 관찰하지 못했다면 첫 post-scroll move는 기준점일 뿐이다. 브라우저가
  // stationary hit-test 뒤 합성한 move를 실제 intent로 오인하지 않는다. movementX/Y가 명시적으로
  // 이동을 보고한 경우만 baseline 없이도 해제한다.
  const moved =
    lastPointerPosition === null
      ? event.movementX !== 0 || event.movementY !== 0
      : event.clientX !== lastPointerPosition.x || event.clientY !== lastPointerPosition.y;
  lastPointerPosition = { x: event.clientX, y: event.clientY };
  if (!moved) return;
  pointerOpenSuppressedAfterScroll = false;
  syncTooltipPointerIntentListeners();
}

function syncTooltipPointerIntentListeners() {
  if (typeof window === "undefined") return;
  // 평상시에는 subscriber가 listener 수명을 소유한다. 다만 scroll latch가 잠긴 0-subscriber 구간에는
  // 실제 pointer 이동을 관찰할 release listener가 남아야 delayed virtualization에도 상태가 이어진다.
  const shouldListen = pointerIntentSubscriberCount > 0 || pointerOpenSuppressedAfterScroll;
  if (shouldListen === pointerIntentListenersAttached) return;

  pointerIntentListenersAttached = shouldListen;
  if (shouldListen) {
    window.addEventListener("scroll", suppressPointerOpenOnScroll, true);
    window.addEventListener("pointermove", resumePointerOpenOnMove, true);
    window.addEventListener("mousemove", resumePointerOpenOnMove, true);
    return;
  }

  window.removeEventListener("scroll", suppressPointerOpenOnScroll, true);
  window.removeEventListener("pointermove", resumePointerOpenOnMove, true);
  window.removeEventListener("mousemove", resumePointerOpenOnMove, true);
  // subscriber도 latch도 없으면 다음 세션은 새 좌표 baseline에서 시작한다.
  lastPointerPosition = null;
}

/** @internal Tooltip/TruncationTooltipHost 공용 post-scroll pointer intent latch. */
export const isTooltipPointerOpenSuppressed = () => pointerOpenSuppressedAfterScroll;

/**
 * @internal boundary event의 좌표가 마지막 실제 위치와 다르면 pointer가 실제로 이동한 것이다.
 * scroll이 만든 stationary boundary event는 같은 좌표라 latch를 유지한다.
 */
export const noteTooltipPointerBoundary = (event?: Pick<MouseEvent, "clientX" | "clientY">) => {
  if (!event) return;
  if (lastPointerPosition === null) {
    // 잠긴 상태에서도 좌표만 baseline으로 기록한다. 이 boundary 자체는 latch를 풀지 않는다.
    lastPointerPosition = { x: event.clientX, y: event.clientY };
    return;
  }
  if (event.clientX === lastPointerPosition.x && event.clientY === lastPointerPosition.y) {
    return;
  }
  lastPointerPosition = { x: event.clientX, y: event.clientY };
  pointerOpenSuppressedAfterScroll = false;
  syncTooltipPointerIntentListeners();
};

/** @internal 실제 window listener는 Tooltip 인스턴스 수와 무관하게 하나만 유지한다. */
export const subscribeTooltipPointerIntent = (): (() => void) => {
  if (typeof window === "undefined") return () => {};

  pointerIntentSubscriberCount += 1;
  syncTooltipPointerIntentListeners();

  let subscribed = true;
  return () => {
    if (!subscribed) return;
    subscribed = false;
    pointerIntentSubscriberCount = Math.max(0, pointerIntentSubscriberCount - 1);
    syncTooltipPointerIntentListeners();
  };
};

export interface TooltipControllerOptions {
  /** `true`면 `show()`가 무효 + 켜지는 순간 강제로 닫는다(앵커가 여는 popup이 떠 있는 동안 억제). */
  suppressed?: boolean;
}

export interface TooltipController {
  visible: boolean;
  show: () => void;
  hide: () => void;
  /** 포인터가 트리거→버블 dead-zone을 건너는 grace 후 닫기(hover bridge). */
  scheduleHide: () => void;
  /** 트리거 요소에 spread — hover(대상별 open delay)/키보드 focus(즉시)로 표시, leave/blur로 닫기. */
  triggerProps: {
    onMouseEnter: (event?: ReactMouseEvent<HTMLElement>) => void;
    onMouseLeave: (event?: ReactMouseEvent<HTMLElement>) => void;
    onFocus: (event: FocusEvent<HTMLElement>) => void;
    onBlur: () => void;
  };
}

// 키보드 intent에서만 표시 — pointer focus와 component lifecycle focus는 제외(WAI-ARIA tooltip 목적).
// Chromium은 키보드 opener 뒤 element.focus()에도 :focus-visible을 이어 줄 수 있어 selector만으로
// scripted focus를 판별할 수 없다. shared marker는 focus ring을 건드리지 않고 transient UI만 막는다.
// happy-dom은 focus-visible 휴리스틱이 없어 모든 focus를 매칭하므로 단위테스트는 selector를 스텁한다.
// TruncationTooltipHost도 같은 focus-intent gate를 재사용한다(중복 휴리스틱 방지).
export const hasTooltipFocusIntent = (el: HTMLElement): boolean => {
  if (isLifecycleFocus(el)) return false;
  try {
    return el.matches(":focus-visible");
  } catch {
    return true; // `:focus-visible` 미지원 환경: a11y 보존을 위해 표시 허용.
  }
};

export function useTooltipController(options?: TooltipControllerOptions): TooltipController {
  const suppressed = options?.suppressed ?? false;
  const [visible, setVisible] = useState(false);
  // setState는 비동기라 타이머 콜백이 "지금 보이는가"를 동기로 못 읽는다 — visibleRef가 진실 소스.
  const visibleRef = useRef(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingScrollCleanupRef = useRef<(() => void) | null>(null);

  const clearPendingScrollDismiss = useCallback(() => {
    pendingScrollCleanupRef.current?.();
    pendingScrollCleanupRef.current = null;
  }, []);

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    clearPendingScrollDismiss();
  }, [clearPendingScrollDismiss]);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  // 즉시 표시 — focus 경로/버블 재진입/manual consumer가 직접 호출. suppressed면 무효.
  const show = useCallback(() => {
    if (suppressed) return;
    clearShowTimer();
    clearHideTimer();
    visibleRef.current = true;
    setVisible(true);
  }, [suppressed, clearShowTimer, clearHideTimer]);

  // 즉시 닫힘. pending open도 함께 취소한다.
  const hide = useCallback(() => {
    clearShowTimer();
    clearHideTimer();
    visibleRef.current = false;
    setVisible(false);
  }, [clearShowTimer, clearHideTimer]);

  // 포인터 open — 새 트리거마다 open delay를 온전히 적용한다. 이전 tooltip의 상태를 전역으로 공유해
  // delay를 건너뛰면 조밀한 세로 리스트에서 bubble이 행마다 즉시 튀어 콘텐츠 탐색을 가린다.
  // pending 동안 scroll이 시작되면 타이머를 취소해 스크롤이 끝난 뒤 stale tooltip이 뜨지 않게 한다.
  const scheduleShow = useCallback(() => {
    if (suppressed || isTooltipPointerOpenSuppressed()) return;
    // 이미 보이는 상태여도 대기 중 grace-hide는 취소한다 — 트리거 밖→안 100ms 내 복귀 시 유지.
    clearHideTimer();
    if (visibleRef.current) return;
    clearShowTimer();
    const dismissPendingOnScroll = () => hide();
    window.addEventListener("scroll", dismissPendingOnScroll, true);
    pendingScrollCleanupRef.current = () =>
      window.removeEventListener("scroll", dismissPendingOnScroll, true);
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      show();
    }, OPEN_DELAY_MS);
  }, [suppressed, clearHideTimer, clearShowTimer, show, hide]);

  // 포인터 close는 유예를 둔다(hover bridge). 대기 중 open은 취소. focus/blur 경로는 즉시(show/hide).
  const scheduleHide = useCallback(() => {
    clearShowTimer();
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      hide();
    }, HOVER_GRACE_MS);
  }, [clearShowTimer, clearHideTimer, hide]);

  // suppressed가 켜지면 hover/focus 상태와 무관하게 즉시 닫는다.
  useEffect(() => {
    if (suppressed) hide();
  }, [suppressed, hide]);

  // 새 target controller도 scroll 직후 합성 boundary enter를 거절할 수 있게 visible/pending 여부와
  // 무관하게 공용 latch를 구독한다(실제 window listener는 모듈당 하나).
  useEffect(() => subscribeTooltipPointerIntent(), []);

  // unmount 시 대기 중 타이머/scroll listener 정리(언마운트 후 setState 방지).
  useEffect(() => {
    return () => {
      clearShowTimer();
      clearHideTimer();
      visibleRef.current = false;
    };
  }, [clearShowTimer, clearHideTimer]);

  const triggerProps = useMemo(
    () => ({
      onMouseEnter: (event?: ReactMouseEvent<HTMLElement>) => {
        noteTooltipPointerBoundary(event);
        scheduleShow();
      },
      onMouseLeave: (event?: ReactMouseEvent<HTMLElement>) => {
        noteTooltipPointerBoundary(event);
        scheduleHide();
      },
      onFocus: (event: FocusEvent<HTMLElement>) => {
        if (event.target instanceof HTMLElement && hasTooltipFocusIntent(event.target)) show();
      },
      onBlur: hide,
    }),
    [scheduleShow, scheduleHide, show, hide],
  );

  return useMemo(
    () => ({ visible, show, hide, scheduleHide, triggerProps }),
    [visible, show, hide, scheduleHide, triggerProps],
  );
}
